import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha,
} from "@mui/material";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { WEBSERVICE } from "../../CoreDataService";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ContainedButton } from "../../Common/Buttons";

const COPYTRADINGSERVICE = WEBSERVICE + "/copyTrading";

const MyPayouts = () => {
  const navigate = useNavigate();
  const { btncolor } = useSelector((state) => state);
  const [payoutHistory, setPayoutHistory] = useState([]);

  useEffect(() => {
    getPayoutHistory();
  }, []);

  const getPayoutHistory = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.get(
        `${COPYTRADINGSERVICE}/apiKey/getPayoutHistory/${userId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `BEARER ${accessToken}`,
          },
        }
      );
      setPayoutHistory(response.data.payOutHistoryList);
    } catch (error) {
      console.error("Error fetching payout history", error);
    }
  };

  return (
    <Box sx={pageWrapper}>
      {/* Hero Banner */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(btncolor || "#1976d2", 0.95)} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <PaymentsOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={heroTitle}>Payments Received</Typography>
            <Typography sx={heroSubtitle}>
              Subscription payments received from copy traders who follow you or your bots
            </Typography>
          </Box>
          <ContainedButton
            variant="contained"
            onClick={() => navigate("/copy-trade")}
            sx={backBtn}
          >
            Back
          </ContainedButton>
        </Box>
      </Box>

      {/* Main Card */}
      <Box sx={mainCard}>
        <Box sx={resultsHeader}>
          <Box>
            <Typography sx={resultsTitle}>Payout History</Typography>
            <Typography sx={resultsCaption}>
              {payoutHistory?.length || 0} payment{payoutHistory?.length !== 1 ? "s" : ""} received
            </Typography>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={modernTableContainer}>
          <Table sx={tableStyle}>
            <TableHead>
              <TableRow sx={modernTableHeadRow}>
                <TableCell sx={modernTableHeadCell}>Date</TableCell>
                <TableCell sx={modernTableHeadCell}>Amount (USDT)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payoutHistory?.length > 0 ? (
                payoutHistory.map((history, index) => (
                  <TableRow key={index} sx={modernTableBodyRow}>
                    <TableCell sx={modernTableBodyCell}>{history.createdOn}</TableCell>
                    <TableCell sx={modernTableBodyCell}>{history.amountInUsd}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} sx={emptyCell}>
                    <InboxOutlinedIcon sx={emptyIcon} />
                    <Typography sx={emptyText}>No payments received yet</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
  flexWrap: "wrap",
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 540,
};

const backBtn = {
  height: 40,
  px: 2.5,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.85rem",
  flexShrink: 0,
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.3)",
  color: "#fff",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.28)" },
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
  p: { xs: 2, md: 2.5 },
};

const resultsHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 1.5,
  mb: 2,
};

const resultsTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const resultsCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.65,
  mt: 0.25,
};

const modernTableContainer = {
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "none",
  overflow: "hidden",
  backgroundColor: "var(--body-color-less)",
};

const tableStyle = {
  minWidth: 400,
};

const modernTableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
};

const emptyCell = {
  textAlign: "center",
  py: 7,
  borderBottom: 0,
};

const emptyIcon = {
  fontSize: 48,
  color: "var(--text-color)",
  opacity: 0.35,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.65,
};

export default MyPayouts;
